import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, UserCheck, Stethoscope, Users, ShieldAlert, ArrowRight } from 'lucide-react';
export function ProblemAgitation() {
  const painPoints = [{
    title: 'Explicás lo mismo 20 veces por semana',
    desc: 'El fraccionamiento, las marcas en la piel, el "no quedo radioactivo" — y cada vez desde cero, sin material de apoyo.'
  }, {
    title: 'El paciente firma el consentimiento sin entenderlo',
    desc: 'Y cuando aparece el efecto adverso, escuchás el "nadie me avisó" que ningún documento mal redactado te ahorra.'
  }, {
    title: 'Las llamadas fuera de horario no paran',
    desc: 'Síntomas esperados que nadie anticipó, sangrados que asustan, fiebre que no saben si es urgencia.'
  }, {
    title: '"¿Cuánto tiempo me queda, doctor?"',
    desc: 'Y no tenés un protocolo para responder sin dar una cifra que el paciente se grabe a fuego.'
  }, {
    title: 'La familia te pide que no le digas nada al paciente',
    desc: 'El pacto de silencio de cada semana, sin una salida estructurada que no rompa el vínculo.'
  }, {
    title: 'El paciente de cérvix abandona antes de la braquiterapia',
    desc: 'Porque nadie le explicó que sin la segunda fase, el tratamiento queda por la mitad.'
  }, {
    title: 'La primera imagen post-RT desata el pánico',
    desc: '"Persiste lesión residual" — y el paciente lee "no funcionó" porque nadie le anticipó la respuesta diferida.'
  }, {
    title: 'El control semanal arranca con 10 minutos de relato desordenado',
    desc: 'Sin registro estructurado, lo importante aparece al final de la consulta — o no aparece.'
  }];
  return <div className="w-full" data-id="element-159">
      {/* Pain Points Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white border-y border-slate-100" data-id="element-160">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-161">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-162">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 sm:mb-4" data-id="element-163">
              ¿TE PASA ESTO?
            </h2>
            <p className="text-lg sm:text-xl text-slate-600" data-id="element-164">
              Si atendés pacientes oncológicos en Argentina, esto te suena
              familiar:
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-12 lg:mb-16" data-id="element-165">
            {painPoints.map((point, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: idx * 0.05
          }} whileHover={{
            scale: 1.01
          }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl bg-red-50/50 border border-red-100 hover:bg-red-50 transition-colors" data-id="element-166">
                <div className="mt-0.5 sm:mt-1 bg-red-100 p-1.5 sm:p-2 rounded-full text-red-600 shrink-0" data-id="element-167">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-168" />
                </div>
                <div data-id="element-169">
                  <h3 className="font-bold text-slate-900 text-base sm:text-lg mb-1" data-id="element-170">
                    {point.title}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600" data-id="element-171">
                    {point.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-172">
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 bg-red-100/50 inline-block px-4 sm:px-6 py-3 rounded-lg border border-red-200" data-id="element-173">
              ¿Cuántos pacientes más vas a atender improvisando la comunicación?
            </p>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-900 text-white" data-id="element-174">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-175">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-176">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3 sm:mb-4" data-id="element-177">
              ¿PARA QUIÉN ES?
            </h2>
            <p className="text-lg sm:text-xl text-teal-400" data-id="element-178">
              Diseñado para vos si querés que tus pacientes entiendan, adhieran
              y confíen
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-179">
            <AudienceCard icon={<UserCheck className="w-6 h-6 sm:w-8 sm:h-8 text-teal-400" data-id="element-181" />} title="Residentes y radioterapeutas jóvenes" desc="Tenés la formación técnica pero nadie te enseñó a comunicar un plan, un consentimiento o una mala noticia." delay={0} data-id="element-180" />
            <AudienceCard icon={<Stethoscope className="w-6 h-6 sm:w-8 sm:h-8 text-teal-400" data-id="element-183" />} title="Radioterapeutas en práctica activa" desc="En hospital público, obra social o práctica privada — querés recuperar tiempo de consulta y bajar las llamadas fuera de horario." delay={0.1} data-id="element-182" />
            <AudienceCard icon={<Users className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" data-id="element-185" />} title="Jefes de servicio y formadores 🆕" desc="Querés estandarizar la comunicación de tu equipo: que residentes, técnicos y médicos hablen el mismo idioma con el paciente." highlight delay={0.2} data-id="element-184" />
            <AudienceCard icon={<ShieldAlert className="w-6 h-6 sm:w-8 sm:h-8 text-teal-400" data-id="element-187" />} title="Especialistas en braquiterapia, SBRT o pediatría" desc="Vas por los dos tomos exclusivos: las técnicas que el paciente no puede ver y el paciente que no es un adulto pequeño." delay={0.3} data-id="element-186" />
          </div>

          <div className="text-center" data-id="element-188">
            <motion.a href="#pricing" whileHover={{
            x: 5
          }} className="inline-flex items-center gap-2 text-sm sm:text-base text-slate-300 hover:text-white transition-colors font-medium" data-id="element-189">
              ¿No estás seguro si es para vos? Tenés 7 días de garantía{' '}
              <ArrowRight className="w-4 h-4" data-id="element-190" />
            </motion.a>
          </div>
        </div>
      </section>
    </div>;
}
function AudienceCard({
  icon,
  title,
  desc,
  highlight = false,
  delay = 0
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  highlight?: boolean;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    delay
  }} whileHover={{
    y: -5
  }} whileTap={{
    scale: 0.98
  }} className={`p-5 sm:p-8 rounded-2xl border cursor-pointer transition-shadow hover:shadow-lg ${highlight ? 'bg-slate-800/80 border-amber-500/50 shadow-amber-900/20' : 'bg-slate-800 border-slate-700'}`} data-id="element-191">
      <div className="mb-4 sm:mb-6" data-id="element-192">{icon}</div>
      <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3" data-id="element-193">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-slate-300 leading-relaxed" data-id="element-194">
        {desc}
      </p>
    </motion.div>;
}